import React from "react";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { ActivityComposer } from "./ActivityComposer";
import { ActivityDetails } from "./ActivityDetails";
import { RichTextEditor } from "components/shared/RichTextEditor";
import { Input } from "components/ui";

const getPlainTextLength = (htmlStr) => {
  if (!htmlStr) return 0;
  if (typeof window !== "undefined") {
    const doc = new DOMParser().parseFromString(htmlStr, "text/html");
    return (doc.body.textContent || "").length;
  }
  return htmlStr.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").length;
};

const DetailsSection = ({
  followUpType,
  selectedLeadObj,
  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  smsBody,
  setSmsBody,
  ccList,
  setCcList,
  bccList,
  setBccList,
  attachments,
  setAttachments,
  phoneNumber,
  setPhoneNumber,
  meetingUrl,
  setMeetingUrl,
  address,
  setAddress,
  detailedNotes,
  setDetailedNotes,
  preferredDeliveryDate,
  setPreferredDeliveryDate,
  preferredDeliveryTime,
  setPreferredDeliveryTime,
  emailTemplate,
  setEmailTemplate,
  title,
  setTitle,
  followUp,
  isScheduleDisabled = false,
  isScheduleDeliveryEnabled = false,
  setIsScheduleDeliveryEnabled = () => {},
  errors = {},
  setErrors = () => {},
  emailTemplates,
  smsTemplates,
  templatesLoading,
}) => {
  const isComposer = followUpType === 1 || followUpType === 2;
  const isDetails = followUpType === 3 || followUpType === 4 || followUpType === 5 || followUpType === 6 || followUpType === 7 || followUpType < 99;
  return (
    <div className="p-4 sm:p-6 space-y-6 col-span-2">
      <div className="flex items-center justify-between mb-4">
      {!isComposer &&   <h4 className="text-sm font-semibold text-gray-900 dark:text-dark-100">
          {isComposer ? 'Composition Details' : 'Activity Details'}
        </h4>}
      </div>

      {isComposer ? (
        <ActivityComposer
        followUp={followUp}
          type={followUpType}
          lead={selectedLeadObj}
          emailSubject={emailSubject}
          setEmailSubject={setEmailSubject}
          emailBody={emailBody}
          setEmailBody={setEmailBody}
          smsBody={smsBody}
          setSmsBody={setSmsBody}
          ccList={ccList}
          setCcList={setCcList}
          bccList={bccList}
          setBccList={setBccList}
          attachments={attachments}
          setAttachments={setAttachments}
          preferredDeliveryDate={preferredDeliveryDate}
          setPreferredDeliveryDate={setPreferredDeliveryDate}
          preferredDeliveryTime={preferredDeliveryTime}
          setPreferredDeliveryTime={setPreferredDeliveryTime}
          emailTemplate={emailTemplate}
          setEmailTemplate={setEmailTemplate}
          isScheduleDisabled={isScheduleDisabled}
          isScheduleDeliveryEnabled={isScheduleDeliveryEnabled}
          setIsScheduleDeliveryEnabled={setIsScheduleDeliveryEnabled}
          errors={errors}
          setErrors={setErrors}
          emailTemplates={emailTemplates}
          smsTemplates={smsTemplates}
          templatesLoading={templatesLoading}
        />
      ) : (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">


          {isDetails ? (
            <ActivityDetails
              type={followUpType}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              meetingUrl={meetingUrl}
              setMeetingUrl={setMeetingUrl}
              address={address}
              setAddress={setAddress}
              notes={detailedNotes}
              setNotes={setDetailedNotes}
              errors={errors}
              setErrors={setErrors}
            />
          ) : (
            /* Common Notes Section with RichTextEditor for Custom activity */
            <div className="space-y-3 pt-0">
              {/* Card for Activity Title */}
              <div className="relative overflow-hidden from-indigo-50/50 to-blue-50/50 dark:from-indigo-950/10 dark:to-blue-950/10 border-2 border-indigo-100/50 dark:border-indigo-900/20 p-4 rounded-3xl bg-white dark:bg-dark-700 shadow-sm border border-gray-100 dark:border-dark-600">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-white dark:bg-dark-700 shadow-sm border border-gray-100 dark:border-dark-600 text-primary-600">
                    <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white leading-none">
                      Activity Title <span className="text-red-500">*</span>
                    </h4>
                    <p className="text-[10px] text-gray-500 dark:text-dark-400 mt-1 font-medium">Please provide a descriptive title for this activity</p>
                  </div>
                </div>

                <div className="relative z-10">
                  <Input
                    type="text"
                    required
                    maxLength={200}
                    error={errors?.title}
                    className="flex h-11 w-full rounded-xl border border-gray-200 bg-white/80 dark:bg-dark-850/80 backdrop-blur-sm px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-dark-600 dark:text-white dark:placeholder:text-dark-400"
                    value={title || ""}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (errors?.title) {
                        setErrors(prev => ({ ...prev, title: null }));
                      }
                    }}
                    placeholder="Enter custom activity title..."
                  />
                  <div className="flex justify-between items-center mt-1 px-1">
                    <span className="text-[10px] text-gray-400">Max 200 characters</span>
                    <span className="text-[10px] text-gray-400 font-semibold">{title?.length || 0}/200</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between px-1">
                <label className="text-sm font-semibold text-gray-800 dark:text-dark-100">
                  Detailed Activity Notes
                </label>
              </div>
              <div className="overflow-hidden">
                <RichTextEditor
                  value={detailedNotes || ""}
                  onChange={(val) => {
                    setDetailedNotes(val);
                    if (errors?.formattedLeadActivityNotes) {
                      setErrors(prev => ({ ...prev, formattedLeadActivityNotes: null }));
                    }
                  }}
                  placeholder="Add detailed notes, outcomes, or next steps about this activity..."
                  height="200px"
                  maxLength={500}
                  limitType="html"
                />
              </div>
              <div className="flex justify-between items-center px-1">
                {errors?.formattedLeadActivityNotes ? (
                  <span className="text-xs text-red-500 font-medium">{errors.formattedLeadActivityNotes}</span>
                ) : (
                  <span className="text-[10px] text-gray-400">Max 500 characters</span>
                )}
                <span className={`text-[10px] font-semibold ${getPlainTextLength(detailedNotes) > 500 ? "text-red-500" : "text-gray-400"}`}>
                  {getPlainTextLength(detailedNotes)}/500
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailsSection;
